import React, { useState } from 'react';
import { Form, Input, Button, DatePicker, Space, Result, App } from 'antd';
import moment from 'moment';
import { API } from '../../config/api'; // Adjust the path based on your project structure
import { POST } from '../../utils/apicalls'; // Adjust the path based on your project structure


interface EligibilityCheckFormProps {
    courseId: number;
    onClose: () => void;
}

const EligibilityCheckForm: React.FC<EligibilityCheckFormProps> = ({ courseId, onClose }) => {
    const [form] = Form.useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [eligibilityResult, setEligibilityResult] = useState(null);
    const { notification } = App.useApp();
    const [eligibilityReason, setEligibilityReason] = useState('');
    const checkEligibility = (values: any) => {

        let isEligible = true;
        let reason = [];
        let strengths = [];
        const birthDate = values.dateOfBirth;
        const today = moment();
        const age = today.diff(birthDate, 'years');
        if (values.qualification && values.qualification.trim() !== '') {
            strengths.push("Qualification requirements met.");
        }
        if (values.experience && values.experience.trim() !== '') {
            strengths.push("Experience requirements met.");
        }

        // More lenient age verification - only check if date is provided
        if (!birthDate) {
            // If no date provided, we still consider eligible
            reason.push("Date of birth not provided, but not required.");
        } else if (age < 18) {
            // For SEE Exam specifically, we still require 18+
            if (values.isSeeExam === 'yes') {
                isEligible = false;
                reason.push("You must be at least 18 years old to sit for SEE Exams.");
            } else {
                // For other courses, we note it but don't make them ineligible
                reason.push("Age is below 18, but may be eligible with guardian consent.");
            }
        } else {
            strengths.push("Age requirement met.");
        }



        // Always eligible unless explicitly determined otherwise
        return {
            isEligible,
            reason: reason.join(' '),
            strengths: strengths.join(' ')
        };
    };

    const handleEligibilitySubmit = async (values: any) => {
        try {
            setIsSubmitting(true);

            // Check eligibility criteria
            const eligibilityCheck: any = checkEligibility(values);

            // Set the eligibility result state
            setEligibilityResult(eligibilityCheck.isEligible);
            setEligibilityReason(eligibilityCheck.reason);

            // Prepare data for API submission
            const submissionData = {
                eligibility_name: values.name,
                eligibility_email: values.email,
                eligibility_qualification: values.qualification,
                eligibility_experiance: values.experience,
                eligibility_phone: values.eligibility_phone,
                eligibility_result: eligibilityCheck.isEligible ? "Eligible" : "Not Eligible",
                eligibility_reason: eligibilityCheck.reason,
                eligibility_dob: values.dateOfBirth.format('YYYY-MM-DD'),
                eligibility_ptin: values.ptin || "N/A",
                Course_id: Number(courseId),
            };

            // Submit to API - uncomment when ready to implement
            const postRequest = await POST(API.CREATE_COURSE_ELIGIBILITY, submissionData);

            // For now, simulate successful API call
            notification.success({
                message: "Eligibility check completed successfully!"
            });

        } catch (error) {
            console.error(error);
            notification.error({
                message: "Something went wrong! Please try again later",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Reset the form and results
    const handleReset = () => {
        form.resetFields();
        setEligibilityResult(null);
        setEligibilityReason('');
    };

    // Additional state for strengths
    const [eligibilityStrengths, setEligibilityStrengths] = useState('');

    // Show the results after submission
    if (eligibilityResult !== null) {
        return (
            <Result
                status={eligibilityResult ? "success" : "info"}
                title={eligibilityResult ? "Congratulations! You are eligible." : "Additional Information Required"}
                subTitle={eligibilityResult ?
                    "You meet the eligibility requirements for this course." :
                    "Please review the following information about your eligibility:"}
                extra={[
                    <div key="details" style={{ textAlign: 'left', marginBottom: '20px' }}>
                        {eligibilityStrengths && (
                            <div style={{ marginBottom: '10px' }}>
                                <h4 style={{ color: '#52c41a' }}>Strengths:</h4>
                                <p>{eligibilityStrengths}</p>
                            </div>
                        )}
                        {eligibilityReason && (
                            <div>
                                <h4 style={{ color: '#faad14' }}>Notes:</h4>
                                <p>{eligibilityReason}</p>
                            </div>
                        )}
                    </div>,
                    <Button type="primary" key="back" onClick={handleReset}>
                        Check Again
                    </Button>,
                    <Button key="close" onClick={onClose}>
                        Close
                    </Button>
                ]}
            />
        );
    }

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleEligibilitySubmit}
            initialValues={{
                isSeeExam: 'no'
            }}
        >
            <Form.Item
                label="Full Name"
                name="name"
                rules={[{ required: true, message: 'Please enter your full name' }]}
            >
                <Input placeholder="Enter your full name" />
            </Form.Item>

            <Form.Item
                label="Email"
                name="email"
                rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Please enter a valid email' }
                ]}
            >
                <Input placeholder="Enter your email address" />
            </Form.Item>
            <Form.Item
                label="Phone Number"
                name="eligibility_phone"
                rules={[
                    { required: true, message: 'Please enter your Phone Number' },

                ]}
            >
                <Input placeholder="Enter your Phone Number" />
            </Form.Item>

            <Form.Item
                label="Date of Birth"
                name="dateOfBirth"
                rules={[{ required: true, message: 'Please select your date of birth' }]}
            >
                <DatePicker
                    style={{ width: '100%' }}
                    disabledDate={(current) => current && current > moment().endOf('day')}
                    placeholder="Select date of birth"
                />
            </Form.Item>

            <Form.Item
                label="Highest Qualification"
                name="qualification"
                rules={[{ required: true, message: 'Please enter your highest qualification' }]}
            >
                <Input placeholder="E.g., Bachelor's in Finance, MBA, etc." />
            </Form.Item>

            <Form.Item
                label="Relevant Experience"
                name="experience"
                rules={[{ required: true, message: 'Please describe your relevant experience' }]}
            >
                <Input.TextArea
                    rows={3}
                    placeholder="Describe your work experience relevant to this course"
                />
            </Form.Item>
            <Form.Item style={{ marginTop: '20px' }}>
                <Space>
                    <Button type="primary" htmlType="submit" loading={isSubmitting}>
                        Check Eligibility
                    </Button>
                    <Button onClick={handleReset}>
                        Reset
                    </Button>
                </Space>
            </Form.Item>
        </Form>
    );
};

export default EligibilityCheckForm;